import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Table, Button, FloatingLabel, Alert, Badge } from "react-bootstrap";
import PageTitle from './components/PageTitle';
import SecondaryFooter from './components/SecondaryFooter';
import { API_ENDPOINTS } from './config/api';

// Add custom styles for pricing cards and animations
const pricingCardStyles = `
  .pricing-card {
    transition: all 0.3s ease;
    border: 2px solid transparent;
    cursor: pointer;
    height: 100%;
  }
  
  .pricing-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  }
  
  .pricing-card.border-primary {
    border-color: #007bff !important;
    background-color: #f8f9ff;
  }
  
  .pricing-card.popular {
    border-color: #007bff;
    position: relative;
  }
  
  .pricing-card .badge {
    top: -10px;
    z-index: 10;
  }
  
  .pricing-card .list-unstyled li {
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(0,0,0,0.05);
  }
  
  .pricing-card .list-unstyled li:last-child {
    border-bottom: none;
  }
  
  .pricing-card .bi-check-circle-fill {
    color: #28a745;
    margin-right: 0.75rem;
    font-size: 1.1rem;
  }
  
  /* Card resizing animations */
  .signup-card {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .signup-card .card-body {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  /* Step transition animations */
  .step-content {
    animation: fadeInUp 0.5s ease-out;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Stepper animation */
  .stepper-container {
    transition: all 0.3s ease;
  }
  
  @media (max-width: 768px) {
    .pricing-card {
      margin-bottom: 1rem;
    }
  }
  
  /* Carousel styling */
  .carousel-control-prev,
  .carousel-control-next {
    width: 10%;
    opacity: 0.8;
  }
  
  .carousel-control-prev-icon,
  .carousel-control-next-icon {
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    padding: 10px;
  }
  
  .carousel-indicators {
    bottom: -40px;
  }
  
  .carousel-indicators button {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: #dee2e6;
    border: none;
    margin: 0 4px;
  }
  
  .carousel-indicators button.active {
    background-color: #007bff;
  }
  
  .carousel-item {
    padding: 0 10px;
  }

  /* Pricing comparison table */
  .pricing-compare {
    border: 1px solid rgba(0,0,0,0.06);
    border-radius: 18px;
    overflow: hidden;
    background: #fff;
  }

  .pricing-compare-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .pricing-compare-table {
    min-width: 760px; /* enables horizontal scroll on small devices */
    margin-bottom: 0;
  }

  .pricing-compare-table th,
  .pricing-compare-table td {
    padding: 16px 14px;
    vertical-align: middle;
  }

  .pricing-compare-table thead th {
    background: #fff;
    border-bottom: 1px solid rgba(0,0,0,0.06);
  }

  .pricing-compare-feature {
    width: 34%;
    font-weight: 600;
    color: #111827;
  }

  .pricing-compare-sticky {
    position: sticky;
    left: 0;
    z-index: 2;
    background: #fff;
    box-shadow: 1px 0 0 rgba(0,0,0,0.06);
  }

  .pricing-compare-plan {
    width: 22%;
    text-align: center;
  }

  .pricing-compare-planHeader {
    cursor: pointer;
    border-radius: 16px;
    transition: transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease;
    padding: 14px 12px;
    border: 1px solid rgba(0,0,0,0.06);
  }

  .pricing-compare-planHeader:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(0,0,0,0.08);
  }

  .pricing-compare-planHeader.isSelected {
    border-color: rgba(99, 102, 241, 0.35);
    box-shadow: 0 14px 30px rgba(99, 102, 241, 0.18);
    background: linear-gradient(180deg, rgba(99, 102, 241, 0.06), rgba(99, 102, 241, 0));
  }

  .pricing-compare-price {
    font-weight: 800;
    letter-spacing: -0.02em;
    font-size: 44px;
    line-height: 1;
    margin: 10px 0 6px;
  }

  .pricing-compare-muted {
    color: rgba(17,24,39,0.62);
    font-size: 12px;
    font-weight: 600;
  }

  .pricing-compare-row {
    border-top: 1px solid rgba(0,0,0,0.06);
  }

  .pricing-compare-row:nth-child(even) td {
    background: rgba(17,24,39,0.02);
  }

  .pricing-compare-icon {
    font-size: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 999px;
  }

  .pricing-compare-icon.ok {
    background: rgba(34,197,94,0.10);
    color: rgb(34,197,94);
  }

  .pricing-compare-icon.no {
    background: rgba(239,68,68,0.10);
    color: rgb(239,68,68);
  }

  .pricing-compare-cta {
    min-width: 96px;
    border-radius: 12px;
    font-weight: 700;
  }
`;

const steps = [
  { label: "Program Details" },
  { label: "Choose Plan" },
  { label: "Payment & Details" },
];

const Stepper = ({ step }) => (
  <div className="d-flex justify-content-center align-items-center mb-5" style={{ maxWidth: 600, margin: "0 auto", position: 'relative' }}>
    {steps.map((s, idx) => (
      <React.Fragment key={s.label}>
        <div className="text-center flex-fill d-flex flex-column align-items-center" style={{ position: 'relative', zIndex: 2 }}>
          <div
            className={`rounded-3 mb-3 mt-3 d-flex align-items-center justify-content-center ${
              idx + 1 === step
                ? "bg-primary text-white"
                : idx + 1 < step
                ? "bg-success text-white"
                : "card-bg-container text-muted"
            }`}
            style={{ width: 36, height: 36, fontWeight: "bold", fontSize: 18 }}
          >
            {idx + 1}
          </div>
          <div style={{ fontSize: 13, fontWeight: idx + 1 === step ? "bold" : "normal" }}>{s.label}</div>
        </div>
        {idx < steps.length - 1 && (
          <div
            style={{
              flex: 1,
              height: 4,
              background: idx + 1 < step ? "var(--green)" : "var(--border-bg)",
              margin: '.5rem 0.25rem',
              minWidth: 40,
              maxWidth: 80,
              alignSelf: 'end',
              borderRadius: 2,
              zIndex: 1,
            }}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Program specialties
const PROGRAM_SPECIALTIES = [
  "General Surgery",
  "Internal Medicine",
  "Family Medicine",
  "Pediatrics",
  "Obstetrics and Gynecology",
  "Psychiatry",
  "Emergency Medicine",
  "Anesthesiology",
  "Radiology",
  "Pathology",
  "Dermatology",
  "Neurology",
  "Orthopedic Surgery",
  "Cardiology",
  "Urology",
  "Ophthalmology",
  "HENT Surgery",
  "Thoracic Surgery",
  "Neurosurgery",
  "Vascular Surgery",
  "Plastic Surgery"
];

// Sample institutions (in a real app, this would come from an API)
const SAMPLE_INSTITUTIONS = [
  "Johns Hopkins Hospital",
  "Mayo Clinic",
  "Cleveland Clinic",
  "Massachusetts General Hospital",
  "UCLA Medical Center",
  "Stanford Health Care",
  "Mount Sinai Hospital",
  "NYU Langone Health",
  "University of Michigan Hospital",
  "Duke University Hospital",
  "Vanderbilt University Medical Center",
  "University of Pennsylvania Hospital",
  "Northwestern Memorial Hospital",
  "University of Chicago Medical Center",
  "Brigham and Women's Hospital",
  "Beth Israel Deaconess Medical Center",
  "University of California San Francisco Medical Center",
  "University of Washington Medical Center",
  "University of Texas MD Anderson Cancer Center",
  "Memorial Sloan Kettering Cancer Center"
];

// Pricing plans
const PRICING_PLANS = [
  {
    id: "single",
    name: "Single Program",
    price: 299,
    period: "month",
    description: "Perfect for single-site programs",
    features: [
      "Core platform modules",
      "Guided onboarding",
      "Email & chat support",
    ],
    comparison: {
      "Number of residents": "30 residents",
      "Program sites": "1 site",
      "Core platform modules": true,
      "Support": "Email & chat",
      "Onboarding": "Guided onboarding",
      "Advanced analytics": false,
      "Custom integrations": false,
      "Dedicated account manager": false,
    },
    popular: false
  },
  {
    id: "multi",
    name: "Multi-site Programs",
    price: 599,
    period: "month",
    description: "Best for growing programs",
    features: [
      "Core platform modules",
      "Personalized onboarding session",
      "Priority email & chat support",
      "Performance analytics & reporting",
    ],
    comparison: {
      "Number of residents": "50 residents",
      "Program sites": "Up to 3 sites",
      "Core platform modules": true,
      "Support": "Priority email & chat",
      "Onboarding": "Personalized onboarding",
      "Advanced analytics": "Performance analytics & reporting",
      "Custom integrations": false,
      "Dedicated account manager": false,
    },
    popular: true
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "For large organizations",
    features: [
      "Core platform modules",
      "Tailored onboarding & training",
      "24/7 priority support",
      "Custom integrations & advanced analytics",
      "Dedicated account manager",
    ],
    comparison: {
      "Number of residents": "Unlimited",
      "Program sites": "Unlimited sites",
      "Core platform modules": true,
      "Support": "24/7 priority support",
      "Onboarding": "Tailored onboarding & training",
      "Advanced analytics": "Advanced analytics suite",
      "Custom integrations": true,
      "Dedicated account manager": true,
    },
    popular: false
  }
];

const buildPricingFeatureMatrix = (plans) => {
  const rowOrder = [
    "Number of residents",
    "Program sites",
    "Core platform modules",
    "Support",
    "Onboarding",
    "Advanced analytics",
    "Custom integrations",
    "Dedicated account manager",
  ];

  const rows = rowOrder
    .filter((feature) => plans.some((plan) => plan.comparison && feature in plan.comparison))
    .map((feature) => {
      const values = plans.map((plan) => plan.comparison?.[feature] ?? false);
      const allIncluded = values.every((v) => v === true);
      return { feature, values, allIncluded };
    });

  return rows;
};

// Helper function to capitalize names (handles compound names like McDonald, O'Neil, Jean-Luc, Ana~Maria)
const capitalizeName = (name) => {
  if (!name) return '';
  return name
    .split(/([ \-~])/g) // Split by space, hyphen, or tilde, but keep the delimiter
    .map(part => {
      if (part === ' ' || part === '-' || part === "'" || part === '~') return part;
      // Handle "Mc" and "Mac" prefixes
      if (/^Mc[a-z]/i.test(part)) {
        return part.charAt(0).toUpperCase() + 'c' + part.charAt(2).toUpperCase() + part.slice(3).toLowerCase();
      }
      if (/^Mac[a-z]/i.test(part)) {
        return part.charAt(0).toUpperCase() + 'ac' + part.charAt(3).toUpperCase() + part.slice(4).toLowerCase();
      }
      // Handle O' prefixes
      if (/^O'[a-z]/i.test(part)) {
        return part.charAt(0).toUpperCase() + "'" + part.charAt(2).toUpperCase() + part.slice(3).toLowerCase();
      }
      // Default: capitalize first letter
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join('');
};

const Signup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Step 1: Basic Info
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // Step 2: Program Details
  const [specialty, setSpecialty] = useState("");
  const [programName, setProgramName] = useState("");
  const [institution, setInstitution] = useState("");
  const [programDuration, setProgramDuration] = useState("");
  const [residentCount, setResidentCount] = useState("");
  
  // Step 3: Plan Selection
  const [selectedPlan, setSelectedPlan] = useState("");
  
  // Step 4: Payment & Details
  const [address, setAddress] = useState({
    street: "",
    apt: "",
    city: "",
    state: "",
    zip: "",
  });
  const [phone, setPhone] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [cardholderName, setCardholderName] = useState("");

  // Check if email already exists
  const checkEmailExists = async (email) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.AUTH}/check-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      return data.exists;
    } catch (error) {
      console.error("Error checking email:", error);
      return false; // Assume email doesn't exist if check fails
    }
  };

  // Validation functions
  const validateStep1 = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password || !confirmPassword) {
      setError("Please fill in all required fields");
      return false;
    }
    
    if (firstName.trim().length < 2) {
      setError("First name must be at least 2 characters long");
      return false;
    }
    
    if (lastName.trim().length < 2) {
      setError("Last name must be at least 2 characters long");
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }
    
    // Check if email already exists
    const emailExists = await checkEmailExists(email);
    if (emailExists) {
      setError("An account with this email address already exists. Please use a different email or try logging in.");
      return false;
    }
    
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(password)) {
      setError("Password does not meet the requirements");
      return false;
    }
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    
    setError("");
    return true;
  };

  const validateStep2 = () => {
    if (!specialty || !programName.trim() || !institution || !programDuration || !residentCount) {
      setError("Please fill in all program details");
      return false;
    }
    
    if (programName.trim().length < 3) {
      setError("Program name must be at least 3 characters long");
      return false;
    }
    
    if (parseInt(residentCount) < 1) {
      setError("Resident count must be at least 1");
      return false;
    }
    
    setError("");
    return true;
  };

  const validateStep3 = () => {
    if (!selectedPlan) {
      setError("Please select a pricing plan");
      return false;
    }
    
    setError("");
    return true;
  };

  const validateStep4 = () => {
    if (!address.street.trim() || !address.city.trim() || !address.state.trim() || !address.zip.trim() || !phone.trim()) {
      setError("Please fill in all required fields");
      return false;
    }
    
    if (!cardNumber.trim() || !expiryDate.trim() || !cvv.trim() || !cardholderName.trim()) {
      setError("Please fill in all payment details");
      return false;
    }
    
    const phoneRegex = /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
    if (!phoneRegex.test(phone.trim())) {
      setError("Please enter a valid phone number");
      return false;
    }
    
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(address.zip.trim())) {
      setError("Please enter a valid ZIP code");
      return false;
    }
    
    setError("");
    return true;
  };

  const handleNext = async (e) => {
    e.preventDefault();
    let isValid = false;
    
    switch (step) {
      case 1:
        isValid = await validateStep1();
        break;
      case 2:
        isValid = validateStep2();
        break;
      case 3:
        isValid = validateStep3();
        break;
      default:
        isValid = true;
    }
    
    if (isValid) {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setError("");
    setStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep4()) return;
    
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_ENDPOINTS.AUTH}/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          password,
          role: "director",
          specialty,
          programName,
          institution,
          programDuration,
          residentCount,
          selectedPlan,
          address,
          phone,
          cardholderName
        })
      });

      const data = await res.json();

      if (res.ok) {
        // Show success message
        alert("Account created successfully! You will be redirected to the login page.");
        // Reload page to login
        window.location.href = "/";
      } else {
        setError(data.message || "Signup failed");
      }
    } catch (err) {
      setError("An error occurred during signup.");
    } finally {
      setLoading(false);
    }
  };

    return (
    <>
      <PageTitle title="Sign Up" description="Create your MedHive account to get started" />
      <style>{pricingCardStyles}</style>
      <Container fluid>
      <div className="d-flex flex-column justify-content-center align-items-center flex-column-fluid min-vh-100">

          <div className="d-flex flex-column justify-content-center align-items-center flex-column-fluid">
          {step === 1 && (
            <div className="py-4 animate-logo">
              <img src="/images/asset11.svg" onClick={() => navigate("/")} alt="MedHive" className="img-fluid mb-3" style={{ height: "60px", cursor: "pointer" }} />
            </div>
          )}
        <Row className="justify-content-center">
        
          {step === 1 ? (
            // Step 1: Small card like login
            <Col xs={12} sm={12} md={8} lg={6} xl={5} className="p-0" style={{ maxWidth: "600px", width: "100%" }}>
              <Card className=" p-md-5 p-4 shadow-sm border-0 rounded-4 signup-card">
                <Card.Header className="p-0 text-center mb-4 border-0">  
                  {/*<img src="/images/asset3.svg" onClick={() => navigate("/")} alt="MedHive" className="img-fluid mb-3" style={{ height: "60px", cursor: "pointer" }} />*/}
                
                  <h5 className="text-center mt-md-0 mt-2 fw-semibold mb-0">
                    Create Your Account
                  </h5>
                  <p className="text-muted mb-0">Start your journey</p>
                </Card.Header>
              <Card.Body className="p-0 mt-3 mb-3 step-content">
                  {error && (
                    <Alert variant="danger" className="text-center mb-3">
                      {error}
                    </Alert>
                  )}

                  <Form onSubmit={handleNext} className="grid-form">
                    <FloatingLabel controlId="firstName" label="First Name">
                      <Form.Control
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(capitalizeName(e.target.value))}
                        placeholder="Enter your first name"
                        required
                      />
                    </FloatingLabel>

                    <FloatingLabel controlId="lastName" label="Last Name">
                      <Form.Control
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(capitalizeName(e.target.value))}
                        placeholder="Enter your last name"
                        required
                      />
                    </FloatingLabel>

                    <FloatingLabel controlId="email" label="Work Email"  style={{ gridColumn: 'span 2' }}>
                      <Form.Control
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your work email"
                        required
                      />
                    </FloatingLabel>

                    <FloatingLabel controlId="password" label="Password">
                      <Form.Control
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Create a password"
                        required
                      />

                    </FloatingLabel>

                    <FloatingLabel controlId="confirmPassword" label="Confirm Password">
                      <Form.Control
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm password"
                        required
                      />
                    </FloatingLabel>
                    {(() => {
                      const isVisible = password.length > 0;
                      const hasLength = password.length >= 8;
                      const hasUpper = /[A-Z]/.test(password);
                      const hasLower = /[a-z]/.test(password);
                      const hasNumber = /\d/.test(password);
                      const hasSpecial = /[@$!%*?&]/.test(password);
                      const item = (ok, label) => (
                        <li className={`d-flex align-items-center gap-2 ${ok ? 'text-success' : 'text-muted'}`}>
                          <i className={`bi ${ok ? 'bi-check-circle-fill' : 'bi-x-circle'}`}></i>
                          <span>{label}</span>
                        </li>
                      );
                      return (
                        <div
                          style={{
                            gridColumn: 'span 2',
                            overflow: 'hidden',
                            transition: 'opacity 200ms ease, max-height 200ms ease, transform 200ms ease',
                            opacity: isVisible ? 1 : 0,
                            maxHeight: isVisible ? 300 : 0,
                            transform: isVisible ? 'translateY(0)' : 'translateY(-4px)'
                          }}
                        >
                          <div className="alert alert-light mb-0">
                            <div className="small mb-2">Password must satisfy all of the following:</div>
                            <ul className="list-unstyled mb-0 small">
                              {item(hasLength, 'At least 8 characters')}
                              {item(hasUpper, 'At least one uppercase letter')}
                              {item(hasLower, 'At least one lowercase letter')}
                              {item(hasNumber, 'At least one number')}
                              {item(hasSpecial, 'At least one special character (@$!%*?&)')}
                            </ul>
                          </div>
                        </div>
                      );
                    })()}

                    <div className="order-2 order-sm-second gap-3 d-flex flex-column flex-sm-row justify-content-between" style={{ gridColumn: 'span 2' }}>
                    <Button 
                      type="submit" 
                      variant="primary" 
                      className="fw-semibold"
                      disabled={loading}
                    >
                      {loading ? "Creating..." : "Continue"}
                    </Button>

                    <div className="d-flex justify-content-center align-items-center gap-2 order-1 order-sm-first login-signup-link">
                      <span className="text-muted small">Already have an account?{" "}</span>
                      <Button 
                        variant="link"
                        size="sm"
                        onClick={() => navigate("/login")}
                        className="text-decoration-none p-0"
                      >
                        Sign in
                      </Button>
                    </div>
                    </div>
                  </Form>
                </Card.Body>
              </Card>

            </Col>
          ) : (
            // Steps 2-4: Full stepper interface
            <Col xs={12} sm={12} md={step === 3 ? 12 : 8} lg={step === 3 ? 12 : 6} xl={step === 3 ? 12 : 4}>
              <div className="mb-4 stepper-container">
                <Stepper step={step - 1} />
              </div>
              <Card className="shadow-sm border-0 rounded-4 signup-card p-md-5 p-4">
                <Card.Header className="p-0 text-center mb-4 border-0 mt-md-0 mt-2">
                <h5 className="text-center mb-0">
                    {step === 2 && "Program Details"}
                    {step === 3 && "Choose Your Plan"}
                    {step === 4 && "Payment & Details"}
                  </h5>
                  <p className="text-muted mb-0">
                      {step === 2 && "Tell us about your program"}
                      {step === 3 && "Select the perfect plan for your needs"}
                      {step === 4 && "Complete your account setup"}
                  </p>
                  </Card.Header>
                <Card.Body className="p-0 mt-3 mb-3 step-content">
                

                  {error && (
                    <Alert variant="danger" className="text-center mb-3">
                      {error}
                    </Alert>
                  )}

                  <Form onSubmit={step === 4 ? handleSubmit : handleNext}>

                {/* Step 2: Program Details */}
                {step === 2 && (
                  <fieldset className="mb-4" key="step-2">
                    <Row className="g-3">
                      <Col md={12}>
                        <FloatingLabel controlId="programName" label="Program Name">
                          <Form.Control
                            type="text"
                            value={programName}
                            onChange={(e) => setProgramName(e.target.value)}
                            placeholder="e.g., General Surgery Residency Program"
                            required
                          />
                        </FloatingLabel>
                      </Col>
                      <Col md={6}>
                        <FloatingLabel controlId="specialty" label="Program Specialty">
                          <Form.Select
                            value={specialty}
                            onChange={(e) => setSpecialty(e.target.value)}
                            required
                          >
                            <option value="">Select specialty...</option>
                            {PROGRAM_SPECIALTIES.map(spec => (
                              <option key={spec} value={spec}>{spec}</option>
                            ))}
                          </Form.Select>
                        </FloatingLabel>
                      </Col>

                      <Col md={6}>
                        <FloatingLabel controlId="institution" label="Institution">
                          <Form.Select
                            value={institution}
                            onChange={(e) => setInstitution(e.target.value)}
                            required
                          >
                            <option value="">Select institution...</option>
                            {SAMPLE_INSTITUTIONS.map(inst => (
                              <option key={inst} value={inst}>{inst}</option>
                            ))}
                          </Form.Select>
                        </FloatingLabel>
                      </Col>
                      <Col md={6}>
                        <FloatingLabel controlId="programDuration" label="Program Duration (years)" className="mb-2">
                          <Form.Select
                            value={programDuration}
                            onChange={(e) => setProgramDuration(e.target.value)}
                            required
                          >
                            <option value="">Select duration...</option>
                            <option value="3">3 years</option>
                            <option value="4">4 years</option>
                            <option value="5">5 years</option>
                            <option value="6">6 years</option>
                            <option value="7">7 years</option>
                          </Form.Select>
                        </FloatingLabel>
                      </Col>
                      <Col md={6}>
                        <FloatingLabel controlId="residentCount" label="Number of Residents" className="mb-3">
                          <Form.Control
                            type="number"
                            value={residentCount}
                            onChange={(e) => setResidentCount(e.target.value)}
                            placeholder="Enter number"
                            min="1"
                            required
                          />
                        </FloatingLabel>
                      </Col>
                    </Row>
                  </fieldset>
                )}

                {/* Step 3: Plan Selection */}
                {step === 3 && (
                  <fieldset className="mb-4" key="step-3">
                    {(() => {
                      const featureRows = buildPricingFeatureMatrix(PRICING_PLANS);
                      const formatPrice = (price) =>
                        typeof price === "number" ? `$${price}` : String(price || "");
                      const formatCellText = (value) => {
                        if (value === true) return "Included";
                        if (value === false) return "Not included";
                        return String(value || "Not included");
                      };

                      const ensureSelected = (planId) => setSelectedPlan(planId);
                      const getCtaLabel = (plan) => (selectedPlan === plan.id ? "Selected" : "Select");

                      return (

                            <Table responsive striped className=" pricing-compare-header-bg custom-modern-responsive ">
                              <thead>
                                <tr className="">
                                  <th className="pricing-compare-feature pricing-compare-sticky" scope="col">
                                    <div className="pricing-compare-muted">Compare plans</div>
                                  </th>
                                  {PRICING_PLANS.map((plan) => {
                                    const isSelected = selectedPlan === plan.id;
                                    return (
                                      <th key={plan.id} className="pricing-compare-plan" scope="col">
                                        <div
                                          className={`pricing-compare-planHeader ${isSelected ? "isSelected" : ""}`}
                                          role="button"
                                          tabIndex={0}
                                          onClick={() => ensureSelected(plan.id)}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") ensureSelected(plan.id);
                                          }}
                                          aria-pressed={isSelected}
                                        >
                                          <div className="d-flex align-items-center justify-content-center gap-2">
                                            <div className="fw-bold">{plan.name}</div>
                                            {plan.popular ? (
                                              <span className="badge text-bg-primary">Pro</span>
                                            ) : null}
                                          </div>
                                          <div className="pricing-compare-price">{formatPrice(plan.price)}</div>
                                          <div className="pricing-compare-muted">{plan.period ? `/${plan.period}` : "\u00A0"}</div>
                                          <div className="mt-3">
                                            <button
                                              type="button"
                                              className={`btn ${isSelected ? "btn-primary" : "btn-outline-primary"} pricing-compare-cta`}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                ensureSelected(plan.id);
                                              }}
                                            >
                                              {getCtaLabel(plan)}
                                            </button>
                                          </div>
                                        </div>
                                      </th>
                                    );
                                  })}
                                </tr>
                              </thead>
                              <tbody>
                                {featureRows.map((row) => (
                                  <tr key={row.feature}>
                                    <td className="pricing-compare-feature pricing-compare-sticky">{row.feature}</td>
                                    {PRICING_PLANS.map((plan, planIndex) => {
                                      const value = row.values[planIndex];
                                      const ok = value === true;
                                      const showCheck = row.allIncluded && ok;
                                      return (
                                        <td key={`${row.feature}-${plan.id}`} className="pricing-compare-plan">
                                          {showCheck ? (
                                            <>
                                              <span className="pricing-compare-icon ok">
                                                <i className="bi bi-check-lg" aria-hidden="true"></i>
                                              </span>
                                              <span className="visually-hidden">Included</span>
                                            </>
                                          ) : (
                                            <span className={ok ? "text-success fw-semibold" : "text-muted"}>
                                              {formatCellText(value)}
                                            </span>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                      );
                    })()}
                  </fieldset>
                )}

                {/* Step 4: Payment & Details */}
                {step === 4 && (
                  <fieldset className="mb-4" key="step-4">
                    <Row className="g-3">
                      <Col md={12}>
                        <h6 className="mb-1 fw-semibold">Billing Address:</h6>
                      </Col>
                      <Col md={9}>
                        <FloatingLabel controlId="street" label="Street Address" >
                          <Form.Control
                            type="text"
                            value={address.street}
                            onChange={(e) => setAddress({ ...address, street: e.target.value })}
                            placeholder="123 Main Street"
                            required
                          />
                        </FloatingLabel>
                      </Col>
                      <Col md={3}>
                        <FloatingLabel controlId="apt" label="Apt/Unit">
                          <Form.Control
                            type="text"
                            value={address.apt}
                            onChange={(e) => setAddress({ ...address, apt: e.target.value })}
                            placeholder="Apt 4B"
                          />
                        </FloatingLabel>
                      </Col>
                      <Col md={4}>
                        <FloatingLabel controlId="city" label="City">
                          <Form.Control
                            type="text"
                            value={address.city}
                            onChange={(e) => setAddress({ ...address, city: e.target.value })}
                            placeholder="New York"
                            required
                          />
                        </FloatingLabel>
                      </Col>
                      <Col md={4}>
                        <FloatingLabel controlId="state" label="State">
                          <Form.Select
                            value={address.state}
                            onChange={(e) => setAddress({ ...address, state: e.target.value })}
                            required
                          >
                            <option value="">Select State...</option>
                            <option value="AL">Alabama</option>
                            <option value="AK">Alaska</option>
                            <option value="AZ">Arizona</option>
                            <option value="AR">Arkansas</option>
                            <option value="CA">California</option>
                            <option value="CO">Colorado</option>
                            <option value="CT">Connecticut</option>
                            <option value="DE">Delaware</option>
                            <option value="DC">District of Columbia</option>
                            <option value="FL">Florida</option>
                            <option value="GA">Georgia</option>
                            <option value="HI">Hawaii</option>
                            <option value="ID">Idaho</option>
                            <option value="IL">Illinois</option>
                            <option value="IN">Indiana</option>
                            <option value="IA">Iowa</option>
                            <option value="KS">Kansas</option>
                            <option value="KY">Kentucky</option>
                            <option value="LA">Louisiana</option>
                            <option value="ME">Maine</option>
                            <option value="MD">Maryland</option>
                            <option value="MA">Massachusetts</option>
                            <option value="MI">Michigan</option>
                            <option value="MN">Minnesota</option>
                            <option value="MS">Mississippi</option>
                            <option value="MO">Missouri</option>
                            <option value="MT">Montana</option>
                            <option value="NE">Nebraska</option>
                            <option value="NV">Nevada</option>
                            <option value="NH">New Hampshire</option>
                            <option value="NJ">New Jersey</option>
                            <option value="NM">New Mexico</option>
                            <option value="NY">New York</option>
                            <option value="NC">North Carolina</option>
                            <option value="ND">North Dakota</option>
                            <option value="OH">Ohio</option>
                            <option value="OK">Oklahoma</option>
                            <option value="OR">Oregon</option>
                            <option value="PA">Pennsylvania</option>
                            <option value="RI">Rhode Island</option>
                            <option value="SC">South Carolina</option>
                            <option value="SD">South Dakota</option>
                            <option value="TN">Tennessee</option>
                            <option value="TX">Texas</option>
                            <option value="UT">Utah</option>
                            <option value="VT">Vermont</option>
                            <option value="VA">Virginia</option>
                            <option value="WA">Washington</option>
                            <option value="WV">West Virginia</option>
                            <option value="WI">Wisconsin</option>
                            <option value="WY">Wyoming</option>
                          </Form.Select>
                        </FloatingLabel>
                      </Col>
                      <Col md={4}>
                        <FloatingLabel controlId="zip" label="ZIP Code">
                          <Form.Control
                            type="text"
                            value={address.zip}
                            onChange={(e) => setAddress({ ...address, zip: e.target.value })}
                            placeholder="12345"
                            required
                          />
                        </FloatingLabel>
                      </Col>
                      <Col md={12}>
                        <FloatingLabel controlId="phone" label="Phone Number">
                          <Form.Control
                            type="text"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="(123) 456-7890"
                            required
                          />
                        </FloatingLabel>
                      </Col>
                      
                      <Col md={12}>
                        <h6 className="mb-1 fw-semibold">Payment Information</h6>
                      </Col>
                      <Col md={12}>
                        <FloatingLabel controlId="cardholderName" label="Cardholder Name">
                          <Form.Control
                            type="text"
                            value={cardholderName}
                            onChange={(e) => setCardholderName(capitalizeName(e.target.value))}
                            placeholder="Name on card"
                            required
                          />
                        </FloatingLabel>
                      </Col>
                      <Col md={12}>
                        <FloatingLabel controlId="cardNumber" label="Card Number">
                          <Form.Control
                            type="text"
                            value={cardNumber}
                            onChange={(e) => setCardNumber(e.target.value)}
                            placeholder="1234 5678 9012 3456"
                            required
                          />
                        </FloatingLabel>
                      </Col>
                      <Col md={6}>
                        <FloatingLabel controlId="expiryDate" label="Expiry Date">
                          <Form.Control
                            type="text"
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(e.target.value)}
                            placeholder="MM/YY"
                            required
                          />
                        </FloatingLabel>
                      </Col>
                      <Col md={6}>
                        <FloatingLabel controlId="cvv" label="CVV">
                          <Form.Control
                            type="text"
                            value={cvv}
                            onChange={(e) => setCvv(e.target.value)}
                            placeholder="123"
                            required
                          />
                        </FloatingLabel>
                      </Col>
                    </Row>
                  </fieldset>
                )}

                <div className="d-flex justify-content-between mt-4">
                  {step > 1 && (
                    <Button variant="secondary" onClick={handleBack} disabled={loading}>
                      Back
                    </Button>
                  )}
                  <div className="flex-grow-1" />
                  {step < 4 && (
                    <Button variant="primary" type="submit" disabled={loading}>
                      Next
                    </Button>
                  )}
                  {step === 4 && (
                    <Button variant="primary" type="submit" disabled={loading}>
                      {loading ? "Creating Account..." : "Create Account"}
                    </Button>
                  )}
                                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      )}
        </Row>
        </div>
        <SecondaryFooter />
        </div>
      </Container>
    </>
  );
};

export default Signup;
